import React from 'react';

import WishlistButton from '@src/stories/blocks/Wishlist/WishlistButton';

import { useEnhancedBlockEvents } from '@src/hooks/useEnhancedBlockEvents';
import { useResponsiveBreakpoints } from '@src/hooks/useMediaQuery';

import { ILiquidProduct } from '@src/types/product';
import useCurrency from '@src/hooks/useCurrency';
import { isRetailerOnly, RETAILER_ONLY_LABEL } from '@src/utilities/retailerOnly';

interface ProductAddToCartWishlistProps {
  product: ILiquidProduct;
  settings?: any;
}

export const ProductAddToCartWishlist = ({ product, settings }: ProductAddToCartWishlistProps) => {
  const {
    button_text,
    button_style,
    button_size,
    button_classes,
    show_price,
    button_width_enable_responsive,
    button_show_icon,
    button_icon_class,
    button_width,
    button_width_mobile,
    button_width_tablet,
    button_width_ultrawide
  } = settings;
  const { isMobile, isTablet, isDesktop, isUltrawide } = useResponsiveBreakpoints();
  const { currentCurrencySymbol } = useCurrency();

  // Get product context and match variants
  const productContext = (window as any).getProductContext?.();
  let variantId = null;
  let matchedVariant: any = null;

  if (productContext && productContext.variantId) {
    variantId = productContext.variantId;
  }

  // Loop through product.variants and match against product context
  if (product && product.variants && productContext) {
    product.variants.forEach(variant => {
      // Match by variant ID
      if (variant.id === variantId || variant.id === productContext.variantId) {
        matchedVariant = variant;
      }
      
      // Also match by current product variants if available
      if (productContext.variants) {
        const contextVariant = productContext.variants.find(v => v.id === variant.id);
        if (contextVariant) {
          matchedVariant = { ...variant, ...contextVariant };
        }
      }
    });
  }

  // Retailer-only wins over sold-out state.
  const retailerOnly = isRetailerOnly(product);
  const soldOut = matchedVariant && matchedVariant.available === false;

  const events = useEnhancedBlockEvents(settings, button_text || 'button', 'button');

  // Check if current breakpoint has actual width value
  const hasWidthSettings = () => {
    if (button_width) return true;
    if (!button_width_enable_responsive) return false;

    if (isMobile && button_width_mobile) return true;
    if (isTablet && button_width_tablet) return true;
    if (isDesktop && button_width) return true;
    if (isUltrawide && button_width_ultrawide) return true;

    return false;
  };

  // Enhanced click handler with actions
  const handleButtonClick = (e: React.MouseEvent) => {
    if (retailerOnly) {
      e.preventDefault();
      return;
    }

    // Trigger configured actions
    events.handleClick({
      buttonText: button_text || 'button',
      buttonUrl: '',
      buttonStyle: '',
      clickTarget: e?.target
    });
  };

  return (
    <div
      className={`add-to-cart-wishlist inline-flex justify-between items-stretch gap-[10px] p-0 overflow-hidden ${
        hasWidthSettings() ? 'w-full' : ''
      } ${button_classes}`}
    >
      <div
        className={`flex items-center justify-center px-0 w-[60px] button button-tertiary button-${button_size}`}
      >
        <WishlistButton
          product={product}
          settings={settings}
          blockSettings={settings}
          iconSize={24}
          type='icon'
        />
      </div>
      <button
        className={`flex-1 button button-${button_style} button-${button_size}`}
        disabled={retailerOnly || soldOut}
        onClick={handleButtonClick}
      >
        {events.isLoading ? (
          <>
            <div className='animate-spin -ml-1 mr-2 h-4 w-4 border-2 border-current border-t-transparent rounded-full'></div>
            <span className='button__label'>Loading...</span>
          </>
        ) : (
          <div className={show_price ? 'flex items-center justify-between w-full' : ''}>
            <div>
              <span className='button__label'>{
                retailerOnly
                  ? RETAILER_ONLY_LABEL
                  : soldOut
                    ? 'Out of stock'
                    : (button_text || 'Add to cart')
              }</span>
              {show_price && !retailerOnly && !soldOut && (
                <span>
                  {currentCurrencySymbol}
                  {matchedVariant 
                    ? (parseInt(matchedVariant.price) / 100).toFixed(2)
                    : (product.price / 100).toFixed(2)
                  }
                </span>
              )}
            </div>
            {button_show_icon && button_icon_class && (
              <i className={`icon-${button_icon_class}`}></i>
            )}
          </div>
        )}
      </button>
    </div>
  );
};
